import { AttemptStatus, type Attempt } from '@/types/attempt';
import { createFileRoute, type SearchSchemaInput } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import SimulationIntro from '../components/SimulationFlow/SimulationIntro';
import SimulationExecution from '../components/SimulationFlow/SimulationExecution';
import SimulationResults from '../components/SimulationFlow/SimulationResults';
import { useGetCollection } from '../hooks/collections/useCollections';
import { useSimulationManager } from '../hooks/useSimulationManager';
import { simulationSession } from '../store/simulationSession';
import { simulationsApi } from '../api/simulations.api';
import { apiClient } from '../api/client';
import type { ApiAttempt, ApiSubmitResult } from '../api/simulations.api';

import Loader from '../components/Loader/Loader';
import GeneralError from '../components/Errors/General';
import SimulationErrorBoundary from '../components/SimulationFlow/SimulationErrorBoundary';
import { CollectionType, type Collection } from '@/types/collection';
import { useAuth } from '@/auth/AuthContext';
import { pushGtmEvent } from '@/lib/gtm';

// This route only ever deals with a real Collection (SIMULATION or
// EXERCISE) — pool-based training attempts live under /esercitazioni/$id —
// so is_custom_exercise is always false here.
function trackStart(
  collection: Collection,
  attemptId: string,
  userId: string | undefined,
  isResume: boolean,
) {
  if (collection.type === CollectionType.SIMULATION) {
    pushGtmEvent({
      event: 'start_simulation',
      collection_id: collection._id,
      collection_name: collection.name,
      attempt_id: attemptId,
      user_id: userId,
      is_resume: isResume,
    });
  } else if (collection.type === CollectionType.EXERCISE) {
    pushGtmEvent({
      event: 'start_exercise',
      collection_id: collection._id,
      collection_name: collection.name,
      attempt_id: attemptId,
      user_id: userId,
      is_resume: isResume,
      is_custom_exercise: false,
    });
  }
}

function toDisplayAttempt(a: ApiAttempt): Attempt {
  return {
    _id: a._id,
    collectionId: a.collectionId,
    status: a.status as AttemptStatus,
    attemptNum: a.attemptNum,
    time: { remaining: a.time.remaining },
    questions: [],
    questionsCount: a.questions.length,
    answeredCount: a.questions.filter((q) => q.alternativeGiven != null).length,
    createdAt: a.time.startTime,
  };
}

export const Route = createFileRoute('/simulazioni/$id')({
  validateSearch: (search: { from?: string } & SearchSchemaInput): { from: string } => ({
    from: search.from ?? 'simulazioni',
  }),
  component: RouteComponent,
  errorComponent: SimulationErrorBoundary,
});

// `translate="no"` prevents browser auto-translation (e.g. Google Translate)
// from mutating the DOM under React inside the simulation flow, which otherwise
// causes `removeChild` crashes when React unmounts/updates translated nodes.
function RouteComponent() {
  return (
    <div
      translate="no"
      className="contents"
    >
      <SimulationRoute />
    </div>
  );
}

function SimulationRoute() {
  const { id } = Route.useParams();
  const { from } = Route.useSearch();
  const backPath = `/${from}` as string;
  const { user } = useAuth();
  const [submitResult, setSubmitResult] = useState<ApiSubmitResult | null>(null);
  const [readyToExecute, setReadyToExecute] = useState(false);

  const sessionAttempt = simulationSession.get();
  const isAttemptRoute = sessionAttempt?._id === id || sessionAttempt?.collectionId === id;

  // `resolvedCollectionId` is known immediately when the session is present.
  // When there is no session (direct link or page refresh), it is resolved async
  // by the query below. State (not ref) ensures the collection re-fetches once resolved.
  const [resolvedCollectionId, setResolvedCollectionId] = useState<string>(
    isAttemptRoute ? sessionAttempt!.collectionId : '',
  );

  // `useGetCollection` is disabled until we have a valid collection ID.
  const { collection, isFetching: isCollectionFetching } = useGetCollection(resolvedCollectionId);

  // Resolve what `id` actually is when there is no session:
  //   1. Try GET /attempts/:id — handles page refresh after create() where id = attemptId.
  //   2. On failure, id must be a collectionId — check for an existing in-progress attempt.
  const { data: resolved, isFetching: isResolving } = useQuery({
    queryKey: ['sim-resolve', id],
    queryFn: async (): Promise<{ collectionId: string; inProgressAttemptId: string | null }> => {
      try {
        const attempt = await simulationsApi.get(id);
        return {
          collectionId: attempt.collectionId,
          inProgressAttemptId: attempt.status === 'IN_PROGRESS' ? attempt._id : null,
        };
      } catch {
        // id is a collectionId — look for an in-progress attempt for this collection.
        try {
          const page = await apiClient.get<{ attempts: Array<{ _id: string; status: string }> }>(
            '/attempts',
            { collectionId: id },
          );
          const inProgress = page.attempts?.find((a) => a.status === 'IN_PROGRESS');
          return { collectionId: id, inProgressAttemptId: inProgress?._id ?? null };
        } catch {
          return { collectionId: id, inProgressAttemptId: null };
        }
      }
    },
    enabled: !isAttemptRoute,
    staleTime: 0,
    retry: false,
  });

  const manager = useSimulationManager({
    collectionId: resolvedCollectionId || id,
    initialAttempt: isAttemptRoute ? sessionAttempt! : undefined,
    timed: collection?.timed ?? true,
    from,
  });

  // Apply the resolved state: update collectionId and load the in-progress attempt if any.
  const [checkDone, setCheckDone] = useState(isAttemptRoute);
  const checkRef = useRef(false);

  useEffect(() => {
    if (isAttemptRoute || checkDone || isResolving || checkRef.current || !resolved) return;
    checkRef.current = true;

    setResolvedCollectionId(resolved.collectionId);

    if (!resolved.inProgressAttemptId) {
      setCheckDone(true);
      return;
    }

    manager.loadAttempt(resolved.inProgressAttemptId)
      .then(() => setCheckDone(true))
      .catch(() => setCheckDone(true));
  }, [isResolving, checkDone, isAttemptRoute, resolved]);

  // Clear the session store once results are shown.
  useEffect(() => {
    if (submitResult) simulationSession.clear();
  }, [!!submitResult]);

  // Block accidental tab close while a simulation is active (not on results screen).
  useEffect(() => {
    if (!manager.attempt || !!submitResult) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [!!manager.attempt, !!submitResult]);

  if (submitResult && manager.attempt) {
    return (
      <SimulationResults
        result={submitResult}
        collection={collection!}
        attempt={manager.attempt}
        backPath={backPath}
      />
    );
  }

  if (!checkDone) return <Loader fullScreen />;
  if (isCollectionFetching && !collection) return <Loader fullScreen />;
  if (!collection) return <GeneralError />;

  if (manager.attempt && readyToExecute) {
    return (
      <SimulationExecution
        collection={collection}
        manager={manager}
        onSubmitSuccess={setSubmitResult}
        backPath={backPath}
      />
    );
  }

  return (
    <SimulationIntro
      collection={collection}
      attempt={manager.attempt ? toDisplayAttempt(manager.attempt) : undefined}
      isResume={!!manager.attempt}
      isLoading={manager.isLoading}
      backPath={backPath}
      onStart={manager.attempt
        ? () => {
            trackStart(collection, manager.attempt!._id, user?.profile.sub, true);
            setReadyToExecute(true);
          }
        : async (extraTimePercent) => {
            const newAttempt = await manager.create(extraTimePercent);
            trackStart(collection, newAttempt._id, user?.profile.sub, false);
            setReadyToExecute(true);
          }
      }
    />
  );
}
